import { Colors, ColorsType } from '@common/tokens';

/** Luminance (0–255) above which a background is considered light. */
const LIGHT_BACKGROUND_THRESHOLD = 150;

/**
 * Picks a readable text color for a background token: dark text on light
 * backgrounds, white on dark ones.
 */
export function getReadableTextColor(color: ColorsType): ColorsType {
  const isLightBackground =
    getPerceivedLuminance(Colors[color]) > LIGHT_BACKGROUND_THRESHOLD;

  return isLightBackground ? 'midnightDarker' : 'white';
}

/** How far the chip border shifts from its background (0–1 mix ratio). */
const BORDER_CONTRAST_RATIO = 0.15;

/**
 * Border color for a chip background token: slightly darker than a light
 * background, slightly lighter than a dark one, so the border stays visible
 * on any token without a hand-picked pair.
 */
export function getChipBorderColor(color: ColorsType): string {
  const hexColor = Colors[color];
  const isLightBackground =
    getPerceivedLuminance(hexColor) > LIGHT_BACKGROUND_THRESHOLD;
  const mixTarget = isLightBackground ? 0 : 255;

  const channels = parseRgbChannels(hexColor).map((channel) =>
    Math.round(channel + (mixTarget - channel) * BORDER_CONTRAST_RATIO)
  );

  return `#${channels
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Perceived luminance (0–255) of a hex color, using ITU-R BT.601 weights. */
function getPerceivedLuminance(hexColor: string): number {
  const [red, green, blue] = parseRgbChannels(hexColor);

  return 0.299 * red + 0.587 * green + 0.114 * blue;
}

function parseRgbChannels(hexColor: string): number[] {
  const hex = expandShorthandHex(hexColor.replace('#', ''));

  return [0, 2, 4].map((offset) => parseInt(hex.slice(offset, offset + 2), 16));
}

/** '#fff'-style shorthand → 'ffffff'-style full form. */
function expandShorthandHex(hex: string): string {
  if (hex.length !== 3) return hex;

  return hex
    .split('')
    .map((char) => char + char)
    .join('');
}
